<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t(`messages.${type}list`) }}</label>
        <div class="blocklist-section">
            <div class="search-input-group">
                <div class="search-input">
                    <img src="/images/search.svg" alt="search" class="search-icon" />
                    <input type="text" class="form-input" v-model="searchQuery" :placeholder="$t('messages.search')" />
                </div>
                <button class="btn-primary" @click="showCreateModal = true">
                    <span class="add-icon">+</span> {{ $t('messages.create') }}
                </button>
            </div>

            <div class="blocklists-container">
                <div class="bordered-container">
                    <div class="blocklists-list">
                        <div v-for="blocklist in filteredBlocklists" :key="blocklist.id" class="blocklist-item"
                            :class="{ 'selected': isBlocklistSelected(blocklist) }">
                            <div class="blocklist-checkbox" :class="{ 'disabled-checkbox': disabled }">
                                <label class="checkbox">
                                    <input type="checkbox" :checked="isBlocklistSelected(blocklist)"
                                        :disabled="disabled"
                                        @change="toggleBlocklist(blocklist)" />
                                </label>
                            </div>
                            <div class="blocklist-content">
                                <div class="blocklist-name">{{ blocklist.name }}</div>
                            </div>
                            <div class="blocklist-actions">
                                <label @click="deleteBlocklist(blocklist)" class="delete-label"
                                    v-tippy="{ content: $t('messages.delete_row'), theme: 'light' }"></label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Модальное окно создания блоклиста -->
        <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t(`messages.create_${type}list`) }}</h3>
                    <button class="btn-close" @click="closeCreateModal">
                        <img src="/images/close.svg" alt="close" />
                    </button>
                </div>

                <div class="modal-body">
                    <div class="form-group">
                        <label class="form-label">{{ $t('messages.name') }}</label>
                        <input type="text" class="form-input" v-model="newBlocklist.name"
                            :placeholder="$t('messages.name_placeholder')" />
                        <div v-if="validationErrors.name" class="error-message">{{ validationErrors.name }}</div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">{{ $t('messages.domains_apps_description') }}</label>
                        <textarea class="form-textarea" v-model="newBlocklist.domainsAppsText" rows="8"
                            :placeholder="$t('messages.domains_apps_placeholder')"></textarea>
                        <div v-if="validationErrors.domains_apps" class="error-message">{{ validationErrors.domains_apps
                            }}</div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button class="btn-primary" @click="createBlocklist" :disabled="isCreating">
                        {{ isCreating ? $t('messages.creating') : $t('messages.create') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BlocklistService from '../../../../../services/BlocklistService.js';

export default {
    name: "Blocklist",
    props: {
        blocklists: {
            type: Array,
            default: () => []
        },
        selectedBlocklists: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            required: true,
            validator: value => ['black', 'white'].includes(value)
        }
    },
    data() {
        return {
            searchQuery: '',
            showCreateModal: false,
            isCreating: false,
            newBlocklist: {
                name: '',
                domainsAppsText: ''
            },
            validationErrors: {}
        };
    },
    computed: {
        filteredBlocklists() {
            if (!this.searchQuery.trim()) {
                return this.blocklists;
            }

            const query = this.searchQuery.toLowerCase();
            return this.blocklists.filter(blocklist =>
                blocklist.name.toLowerCase().includes(query)
            );
        }
    },
    methods: {
        isBlocklistSelected(blocklist) {
            return this.selectedBlocklists.some(item => item.id === blocklist.id);
        },

        toggleBlocklist(blocklist) {
            if (this.disabled) {
                return;
            }
            this.$emit('toggle-blocklist', blocklist);
        },

        async createBlocklist() {
            this.clearValidationErrors();

            const domainsApps = BlocklistService.parseDomainsApps(this.newBlocklist.domainsAppsText);

            const blocklistData = {
                name: this.newBlocklist.name,
                type: this.type,
                domains_apps: domainsApps
            };

            const validation = BlocklistService.validateBlocklistData(blocklistData);

            if (!validation.isValid) {
                this.setValidationErrors(validation.errors);
                return;
            }

            this.isCreating = true;

            try {
                const response = await BlocklistService.createBlocklist(blocklistData);
                this.$emit('blocklist-created', response.data);
                this.closeCreateModal();
            } catch (error) {
                this.$emit('error', error.message);
            } finally {
                this.isCreating = false;
            }
        },

        async deleteBlocklist(blocklist) {
            if (!confirm(`${this.$t('messages.confirm_delete_blocklist')} "${blocklist.name}"?`)) {
                return;
            }

            try {
                await BlocklistService.deleteBlocklist(blocklist.id);
                this.$emit('blocklist-deleted', blocklist);
            } catch (error) {
                this.$emit('error', error.message);
            }
        },

        closeCreateModal() {
            this.showCreateModal = false;
            this.newBlocklist = {
                name: '',
                domainsAppsText: ''
            };
            this.clearValidationErrors();
        },

        setValidationErrors(errors) {
            this.validationErrors = {};
            errors.forEach(error => {
                if (error.includes('Название')) {
                    this.validationErrors.name = error;
                } else if (error.includes('домен') || error.includes('приложение')) {
                    this.validationErrors.domains_apps = error;
                }
            });
        },

        clearValidationErrors() {
            this.validationErrors = {};
        }
    }
};
</script>

<style lang="scss" scoped>
.search-input-group {
    display: flex;
    gap: 12px;
    align-items: center;
}

.blocklist-section {
    width: 50%;

    .blocklists-container {
        margin-top: 16px;

        .bordered-container {
            border: 1px solid #e9ecef;
            border-radius: 2px;
            background: white;
        }

        .blocklist-item {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-bottom: 1px solid #e9ecef;
            transition: all 0.2s;

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background: #f8f9fa;
            }

            .blocklist-checkbox {
                margin-right: 12px;
            }

            .disabled-checkbox input {
                opacity: 0.6;
                cursor: not-allowed;
            }

            .blocklist-content {
                flex: 1;

                .blocklist-name {
                    font-weight: 500;
                    font-size: 14px;
                    color: #212529;
                    margin: 0;
                }
            }

            .blocklist-actions {
                margin-left: auto;

                .btn-delete {
                    width: 24px;
                    height: 24px;
                    border: none;
                    background: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.2s;

                    &:hover {
                        background: #f8f9fa;
                    }

                    img {
                        width: 14px;
                        height: 14px;
                        opacity: 0.6;
                    }
                }
            }
        }
    }
}

// Стили для модальных окон
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e9ecef;

    h3 {
        font-weight: 700;
        font-size: 18px;
        color: #212529;
        margin: 0;
    }

    .btn-close {
        width: 24px;
        height: 24px;
        border: none;
        background: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 16px;
            height: 16px;
            opacity: 0.6;
        }

        &:hover img {
            opacity: 1;
        }
    }
}

.modal-body {
    padding: 24px;

    .form-group {
        margin-bottom: 20px;

        &:last-child {
            margin-bottom: 0;
        }

        .form-label {
            display: block;
            font-weight: 500;
            font-size: 14px;
            color: #212529;
            margin-bottom: 8px;
        }

        .form-input {
            width: 100%;
            height: 40px;
            padding: 8px 12px;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            font-size: 14px;
            background: white;

            &:focus {
                outline: none;
                border-color: #007bff;
                box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
            }
        }

        .form-textarea {
            width: 100%;
            padding: 12px;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            font-size: 14px;
            background: white;
            resize: vertical;
            font-family: inherit;

            &:focus {
                outline: none;
                border-color: #007bff;
                box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
            }
        }

        .error-message {
            color: #dc3545;
            font-size: 12px;
            margin-top: 4px;
        }
    }
}

.modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #e9ecef;
    display: flex;
    justify-content: flex-end;

    // Стили кнопки теперь импортируются из _buttons.scss
}
</style>
